import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Badge,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemButton,
  Autocomplete,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ForumIcon from '@mui/icons-material/Forum';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DraftsIcon from '@mui/icons-material/Drafts';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { messagingService } from '../../services/MessagingService';
import {
  consumePendingOpenMessageContactId,
  setActiveMessageChatContactId,
} from '../../services/messageNotificationService';
import { GROUP_MESSAGE_TO_USER_ID, Message, MessageConversationMeta } from '../../types/typesMessages';
import { useAuth } from '../../services/AuthContext';
import { getRoleLabel } from '../../utils/roleTranslations';
import { useSyncService } from '../../services/DataSyncService';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { hybridApiBaseUrl } from '../../services/hybridFlags';
import { fetchRepairsFromHybrid } from '../../services/entitiesHybridService';
import { useDebouncedValue } from '../../hooks/useDebouncedSearchField';

interface MessagesDialogProps {
  open: boolean;
  onClose: () => void;
  /** פתיחה ישירה לשיחה (למשל מלחיצה על התראת מערכת) */
  initialContactId?: string | null;
}

const GROUP_THREAD_PREFIX = 'group:';
const BROADCAST_THREAD_ID = 'broadcast:multi';

const isGroupMessage = (msg: Message): boolean => {
  if (msg.conversationType === 'direct') return false;
  return (
    String(msg.toUserId || '') === GROUP_MESSAGE_TO_USER_ID || msg.conversationType === 'group'
  );
};

/** מזהה שיחת קבוצה גם אם ההודעה נשמרה בטעות כ-1:1 (לפני מיגרציה) */
const isGroupMessageLoose = (msg: Message, knownGroupConvIds: Set<string>): boolean => {
  const convId = String(msg.conversationId || '').trim();
  if (convId && knownGroupConvIds.has(convId)) return true;
  return isGroupMessage(msg);
};

const getMessageThreadKey = (msg: Message, knownGroupConvIds?: Set<string>): string => {
  const convId = String(msg.conversationId || '').trim();
  if (isGroupMessageLoose(msg, knownGroupConvIds || new Set())) {
    if (convId) return `${GROUP_THREAD_PREFIX}${convId}`;
    const title = String(msg.conversationTitle || msg.toUserName || '').trim();
    if (title) return `${GROUP_THREAD_PREFIX}title:${title}`;
  }
  const otherId = msg.isSent ? msg.toUserId : msg.fromUserId;
  return otherId;
};

/** מאחד group:title:שם עם group:{conversationId} לפי מפתח שרת (רק לקבוצות — לא לשיחות ישירות) */
const resolveGroupThreadKey = (
  msg: Message,
  knownGroupConvIds: Set<string>,
  titleToConvId: Map<string, string>,
): string => {
  let threadKey = getMessageThreadKey(msg, knownGroupConvIds);
  if (!threadKey.startsWith(GROUP_THREAD_PREFIX)) return threadKey;

  if (threadKey.startsWith(`${GROUP_THREAD_PREFIX}title:`)) {
    const title = threadKey.slice(`${GROUP_THREAD_PREFIX}title:`.length);
    const mapped = titleToConvId.get(title);
    if (mapped) return `${GROUP_THREAD_PREFIX}${mapped}`;
  }

  const title = String(msg.conversationTitle || msg.toUserName || '').trim();
  if (title) {
    const mapped = titleToConvId.get(title);
    if (mapped) return `${GROUP_THREAD_PREFIX}${mapped}`;
  }

  return threadKey;
};

const pickLatestMessage = (messages: Message[]): Message | undefined => {
  if (!messages.length) return undefined;
  const sorted = [...messages].sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
  return sorted[sorted.length - 1];
};

interface Conversation {
  contactId: string;
  contactName: string;
  contactPhoto?: string;
  contactRole?: string;
  isGroup?: boolean;
  conversationId?: string;
  messages: Message[];
  /** חסר רק בשיחה סינתטית לפני ההודעה הראשונה */
  lastMessage?: Message;
  unreadCount: number;
}

interface MessageContactOption {
  id: string;
  fullName: string;
  email: string;
  role: string;
  photoURL?: string;
}

interface QuickRepairOption {
  id: string;
  callNumber: string;
  customerName?: string;
  deviceModel?: string;
  status?: string;
}

const AVATAR_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#009688', '#4caf50', '#ff9800',
];

const getAvatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const contactOptionLabel = (option: MessageContactOption) =>
  option.fullName || option.email || 'משתמש';

const isSameContactOption = (a: MessageContactOption, b: MessageContactOption) =>
  a.id === b.id;

const toMs = (date: unknown): number =>
  new Date(date as string | number | Date).getTime();

const formatTime = (date: unknown) =>
  new Date(date as string | number | Date).toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
  });

const formatConvDate = (date: unknown) => {
  const d = new Date(date as string | number | Date);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0)
    return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'אתמול';
  if (diffDays < 7) return d.toLocaleDateString('he-IL', { weekday: 'short' });
  return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
};

const resolveAttachmentUrl = (rawUrl?: string): string => {
  const input = String(rawUrl || '').trim();
  if (!input) return '';
  const mediaIndex = input.indexOf('/media/');
  if (mediaIndex < 0) return input;
  const mediaPath = input.slice(mediaIndex);
  const base = String(hybridApiBaseUrl || '').replace(/\/+$/, '');
  if (base) return `${base}${mediaPath}`;
  return mediaPath;
};

const MessagesDialog: React.FC<MessagesDialogProps> = ({ open, onClose, initialContactId = null }) => {
  const { user, role } = useAuth();
  const { employees } = useSyncService();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [messages, setMessages] = useState<Message[]>([]);
  /** טעינת רשימת הודעות מהשרת — לא חוסם את כל הדיאלוג */
  const [messagesListLoading, setMessagesListLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  /** שיחה חדשה לפני הודעה ראשונה — עדיין לא מופיעה ב־conversations */
  const [pendingContactThread, setPendingContactThread] = useState<MessageContactOption | null>(null);
  const [pendingGroupThread, setPendingGroupThread] = useState<{
    threadId: string;
    conversationId: string;
    title: string;
    members: MessageContactOption[];
  } | null>(null);
  const [pendingBroadcastContacts, setPendingBroadcastContacts] = useState<MessageContactOption[] | null>(
    null,
  );
  const [multiPickerMode, setMultiPickerMode] = useState(false);
  const [multiPickerValue, setMultiPickerValue] = useState<MessageContactOption[]>([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupTitleInput, setGroupTitleInput] = useState('');
  const [groupMembersInput, setGroupMembersInput] = useState<MessageContactOption[]>([]);
  const [groupApiMembers, setGroupApiMembers] = useState<{ userId: string; userName: string }[]>([]);
  const [serverGroupConversations, setServerGroupConversations] = useState<MessageConversationMeta[]>([]);
  const [groupMembersCacheTick, setGroupMembersCacheTick] = useState(0);
  const [contactPickerInput, setContactPickerInput] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const broadcastRecipientsRef = useRef<MessageContactOption[] | null>(null);
  /** חברי קבוצה לפי מפתח שיחה / conversationId — נשמר גם אחרי שליחה ראשונה */
  const groupMembersCacheRef = useRef<Map<string, MessageContactOption[]>>(new Map());
  const [inlineAttachment, setInlineAttachment] = useState<File | null>(null);
  const [uploadingInlineAttachment, setUploadingInlineAttachment] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageName, setPreviewImageName] = useState<string>('');
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [activeMessageForActions, setActiveMessageForActions] = useState<Message | null>(null);
  const [repairPickerOpen, setRepairPickerOpen] = useState(false);
  const [repairSearch, setRepairSearch] = useState('');
  const debouncedRepairSearch = useDebouncedValue(repairSearch, 350);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairOptions, setRepairOptions] = useState<QuickRepairOption[]>([]);
  /** ללא כפילות: subscribe כבר טוען מיד פעם אחת — לא מריצים GET נוסף באותה פתיחה */
  const messagesInitialLoadRef = useRef(false);
  /** אחרי «חזרה» במובייל — לא לבחור שוב אוטומטית את השיחה האחרונה */
  const suppressAutoSelectLastConvRef = useRef(false);
  const prevDialogOpenRef = useRef(false);

  // ─── Load & subscribe ───────────────────────────────────────────────────────

  const loadMessages = useCallback(async (): Promise<Message[]> => {
    if (!user?.uid) return [];
    try {
      messagingService.invalidateUserMessagesCache(user.uid);
      const list = await messagingService.getUserMessages(user.uid);
      setMessages(list);
      return list;
    } catch (err) {
      console.error('Error loading messages:', err);
      return [];
    }
  }, [user?.uid]);

  const refreshServerGroupConversations = useCallback(async () => {
    if (!user?.uid) return;
    const list = await messagingService.getUserConversations(user.uid);
    setServerGroupConversations(list.filter((c) => c.type === 'group'));
  }, [user?.uid]);

  useEffect(() => {
    if (!open || !user?.uid) return;
    void refreshServerGroupConversations();
    void messagingService.probeGroupApiSupport(user.uid, true);
  }, [open, user?.uid, refreshServerGroupConversations]);

  useEffect(() => {
    if (!open || !user?.uid) return;
    messagesInitialLoadRef.current = false;
    setMessagesListLoading(true);
    const unsub = messagingService.subscribeToMessages(user.uid, (list) => {
      setMessages(list);
      if (!messagesInitialLoadRef.current) {
        messagesInitialLoadRef.current = true;
        setMessagesListLoading(false);
      }
    });
    return () => unsub();
  }, [open, user?.uid]);

  useEffect(() => {
    if (!open) {
      setContactPickerInput('');
      setSelectedContactId(null);
      setPendingContactThread(null);
      setActiveMessageChatContactId(null);
      return;
    }
    const pending = consumePendingOpenMessageContactId();
    const contactToOpen = String(initialContactId || pending || '').trim();
    if (contactToOpen) setSelectedContactId(contactToOpen);
  }, [open, initialContactId]);

  useEffect(() => {
    if (!open) return;
    setActiveMessageChatContactId(selectedContactId);
  }, [open, selectedContactId]);

  // ─── Group messages into conversations ──────────────────────────────────────

  const resolveContactById = useCallback(
    (userId: string) => {
      const id = String(userId || '').trim();
      if (!id) return null;
      const emp = employees.get(id);
      if (!emp) return null;
      const data: any = emp;
      return {
        name: data.fullName || data.name || data.displayName || data.email || '',
        photo: data.photoURL as string | undefined,
        role: data.role as string | undefined,
      };
    },
    [employees],
  );

  const knownGroupConvIds = useMemo(
    () => new Set(serverGroupConversations.map((c) => String(c.id || '').trim()).filter(Boolean)),
    [serverGroupConversations],
  );

  const conversations = useMemo((): Conversation[] => {
    const map = new Map<string, Conversation>();
    const titleToConvId = new Map<string, string>();
    serverGroupConversations.forEach((gc) => {
      const convId = String(gc.id || '').trim();
      const title = String(gc.title || '').trim();
      if (convId && title) titleToConvId.set(title, convId);
    });

    serverGroupConversations.forEach((gc) => {
      const convId = String(gc.id || '').trim();
      if (!convId) return;
      const threadKey = `${GROUP_THREAD_PREFIX}${convId}`;
      const members = (gc.members || []).map((m) => ({
        id: m.userId,
        fullName: m.userName || 'משתמש',
        email: '',
        role: m.userRole || 'viewer',
        photoURL: m.userPhotoURL,
      }));
      groupMembersCacheRef.current.set(threadKey, members);
      groupMembersCacheRef.current.set(convId, members);

      const existing = map.get(threadKey);
      if (existing) {
        existing.contactName = String(gc.title || '').trim() || existing.contactName;
        existing.isGroup = true;
        existing.conversationId = convId;
        return;
      }

      map.set(threadKey, {
        contactId: threadKey,
        contactName: String(gc.title || '').trim() || 'קבוצה',
        isGroup: true,
        conversationId: convId,
        messages: [],
        lastMessage: undefined,
        unreadCount: 0,
      });
    });

    messages.forEach((msg) => {
      const threadKey = resolveGroupThreadKey(msg, knownGroupConvIds, titleToConvId);
      const isGroup = isGroupMessageLoose(msg, knownGroupConvIds);
      const otherId = msg.isSent ? msg.toUserId : msg.fromUserId;
      const resolved = !isGroup ? resolveContactById(otherId) : null;
      let displayName = isGroup
        ? String(msg.conversationTitle || msg.toUserName || 'קבוצה').trim() || 'קבוצה'
        : msg.isSent
          ? msg.toUserName
          : msg.fromUserName;
      if (resolved?.name) displayName = resolved.name;
      const otherPhoto =
        !msg.isSent && !isGroup
          ? (resolved?.photo ?? msg.fromUserPhotoURL ?? undefined)
          : undefined;
      const otherRole =
        !msg.isSent && !isGroup ? (resolved?.role ?? msg.fromUserRole) : undefined;

      if (!map.has(threadKey)) {
        map.set(threadKey, {
          contactId: threadKey,
          contactName: displayName,
          contactPhoto: otherPhoto,
          contactRole: otherRole,
          isGroup,
          conversationId: isGroup ? String(msg.conversationId || '').trim() || undefined : undefined,
          messages: [],
          lastMessage: msg,
          unreadCount: 0,
        });
      }

      const conv = map.get(threadKey)!;
      if (threadKey.startsWith(GROUP_THREAD_PREFIX)) conv.isGroup = true;
      conv.messages.push(msg);

      if (!msg.isSent && !isGroup) {
        if (msg.fromUserPhotoURL) conv.contactPhoto = msg.fromUserPhotoURL;
        if (msg.fromUserRole) conv.contactRole = msg.fromUserRole;
      }
      if (isGroup && msg.conversationTitle) conv.contactName = msg.conversationTitle;
      const msgTime = toMs(msg.createdAt);
      const lastTime = conv.lastMessage ? toMs(conv.lastMessage.createdAt) : 0;
      if (!conv.lastMessage || (Number.isFinite(msgTime) && msgTime >= lastTime)) {
        conv.lastMessage = msg;
      }
      if (!msg.isSent && !msg.isRead) conv.unreadCount++;
    });

    return Array.from(map.values())
      .map((conv) => {
        const sortedMessages = [...conv.messages].sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt));
        const lastMessage = conv.lastMessage || pickLatestMessage(sortedMessages);
        return {
          ...conv,
          messages: sortedMessages,
          lastMessage,
        };
      })
      .filter((conv) => !conv.isGroup || Boolean(conv.lastMessage) || conv.messages.length > 0)
      .sort((a, b) => {
        const ta = a.lastMessage ? toMs(a.lastMessage.createdAt) : 0;
        const tb = b.lastMessage ? toMs(b.lastMessage.createdAt) : 0;
        return tb - ta;
      });
  }, [messages, resolveContactById, serverGroupConversations, knownGroupConvIds]);

  const messageContactOptions = useMemo((): MessageContactOption[] => {
    return Array.from(employees.values())
      .filter((employee) => (employee as { role?: string }).role !== 'former_employee')
      .map((employee) => {
        const data: any = employee;
        return {
          id: employee.id,
          fullName: data.fullName || data.name || data.displayName || '',
          email: data.email || '',
          role: data.role || 'viewer',
          photoURL: data.photoURL || undefined,
        };
      })
      .filter((u) => u.id !== user?.uid && u.role !== 'external_viewer')
      .sort((a, b) => {
        const nameA = a.fullName || a.email || '';
        const nameB = b.fullName || b.email || '';
        return nameA.localeCompare(nameB, 'he');
      });
  }, [employees, user?.uid]);

  const selectedGroupMemberLabels = useMemo((): { id: string; name: string }[] => {
    const conv = conversations.find((c) => c.contactId === selectedContactId);
    const isGroupChat =
      Boolean(conv?.isGroup) ||
      Boolean(selectedContactId?.startsWith(GROUP_THREAD_PREFIX)) ||
      Boolean(pendingGroupThread?.threadId === selectedContactId);
    if (!isGroupChat) return [];

    const byId = new Map<string, string>();
    const addMember = (id: string, name: string) => {
      const uid = String(id || '').trim();
      if (!uid) return;
      byId.set(uid, String(name || '').trim() || 'משתמש');
    };

    const cacheKeys = [
      selectedContactId || '',
      conv?.conversationId ? `${GROUP_THREAD_PREFIX}${conv.conversationId}` : '',
      conv?.conversationId || '',
    ];
    cacheKeys.forEach((key) => {
      (groupMembersCacheRef.current.get(key) || []).forEach((m) => {
        addMember(m.id, m.fullName || m.email);
      });
    });

    groupApiMembers.forEach((m) => addMember(m.userId, m.userName));

    if (pendingGroupThread?.threadId === selectedContactId) {
      pendingGroupThread.members.forEach((m) => addMember(m.id, m.fullName || m.email));
    }

    if (user?.uid) addMember(user.uid, user.displayName || user.email || 'אני');

    messages
      .filter((m) => getMessageThreadKey(m, knownGroupConvIds) === selectedContactId)
      .forEach((msg) => {
        addMember(msg.fromUserId, resolveContactById(msg.fromUserId)?.name || msg.fromUserName);
      });

    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'he'));
  }, [
    selectedContactId,
    conversations,
    pendingGroupThread,
    groupApiMembers,
    messages,
    user,
    resolveContactById,
    groupMembersCacheTick,
    knownGroupConvIds,
  ]);

  useEffect(() => {
    const conv = conversations.find((c) => c.contactId === selectedContactId);
    const convId = conv?.conversationId;
    if (!open || !convId || !user?.uid) {
      setGroupApiMembers([]);
      return;
    }
    void (async () => {
      const list = await messagingService.getUserConversations(user.uid);
      const found = list.find((c) => c.id === convId && c.type === 'group');
      const members = (found?.members || []).map((m) => ({
        userId: m.userId,
        userName: m.userName || 'משתמש',
      }));
      setGroupApiMembers(members);
      if (members.length > 0 && selectedContactId) {
        const asOptions: MessageContactOption[] = members.map((m) => ({
          id: m.userId,
          fullName: m.userName,
          email: '',
          role: 'viewer',
        }));
        groupMembersCacheRef.current.set(selectedContactId, asOptions);
        groupMembersCacheRef.current.set(`${GROUP_THREAD_PREFIX}${convId}`, asOptions);
        groupMembersCacheRef.current.set(convId, asOptions);
        setGroupMembersCacheTick((t) => t + 1);
      }
    })();
  }, [open, selectedContactId, conversations, user?.uid]);

  const selectedConv = useMemo((): Conversation | null => {
    const fromList = conversations.find((c) => c.contactId === selectedContactId);
    if (fromList) return fromList;
    if (pendingGroupThread && pendingGroupThread.threadId === selectedContactId) {
      return {
        contactId: pendingGroupThread.threadId,
        contactName: pendingGroupThread.title,
        isGroup: true,
        conversationId: pendingGroupThread.conversationId || undefined,
        messages: [],
        unreadCount: 0,
      };
    }
    if (pendingBroadcastContacts && selectedContactId === BROADCAST_THREAD_ID) {
      const names = pendingBroadcastContacts
        .map((c) => c.fullName || c.email)
        .slice(0, 3)
        .join(', ');
      const extra = pendingBroadcastContacts.length > 3 ? ` +${pendingBroadcastContacts.length - 3}` : '';
      return {
        contactId: BROADCAST_THREAD_ID,
        contactName: `שליחה ל־${pendingBroadcastContacts.length} אנשים (${names}${extra})`,
        messages: [],
        unreadCount: 0,
      };
    }
    if (pendingContactThread && pendingContactThread.id === selectedContactId) {
      return {
        contactId: pendingContactThread.id,
        contactName: pendingContactThread.fullName || pendingContactThread.email,
        contactPhoto: pendingContactThread.photoURL,
        contactRole: pendingContactThread.role,
        messages: [],
        unreadCount: 0,
      };
    }
    return null;
  }, [conversations, selectedContactId, pendingContactThread, pendingGroupThread, pendingBroadcastContacts]);

  const totalUnread = useMemo(
    () => conversations.reduce((s, c) => s + c.unreadCount, 0),
    [conversations],
  );

  useEffect(() => {
    if (!pendingContactThread || !selectedContactId) return;
    if (conversations.some((c) => c.contactId === pendingContactThread.id)) {
      setPendingContactThread(null);
    }
  }, [conversations, pendingContactThread, selectedContactId]);

  // ─── Auto-scroll to bottom ───────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedConv) return;
    const t = setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    return () => clearTimeout(t);
  }, [selectedConv, selectedContactId]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const focusReplyInput = useCallback(() => {
    window.setTimeout(() => {
      replyInputRef.current?.focus();
    }, 120);
  }, []);

  useEffect(() => {
    if (!open || !pendingGroupThread) return;
    if (selectedContactId === pendingGroupThread.threadId) {
      focusReplyInput();
    }
  }, [open, pendingGroupThread, selectedContactId, focusReplyInput]);

  const handleSelectConv = useCallback(
    async (contactId: string) => {
      const keepPendingGroup =
        pendingGroupThread?.threadId && pendingGroupThread.threadId === contactId;
      setPendingContactThread(null);
      if (!keepPendingGroup) setPendingGroupThread(null);
      setPendingBroadcastContacts(null);
      setSelectedContactId(contactId);
      setReplyText('');
      const conv = conversations.find((c) => c.contactId === contactId);
      if (!conv || !user?.uid) return;

      if (conv.isGroup && conv.conversationId) {
        const toMark = conv.messages.filter((m) => !m.isSent && !m.isRead && !m.isDeleted);
        const nowIso = new Date().toISOString();
        if (toMark.length > 0) {
          setMessages((prev) =>
            prev.map((m) =>
              toMark.some((x) => x.id === m.id)
                ? { ...m, isRead: true, readAt: m.readAt ?? nowIso }
                : m,
            ),
          );
          messagingService.decreaseUnreadCountNow(user.uid, toMark.length);
        }
        try {
          await messagingService.markConversationAsRead(conv.conversationId, user.uid);
        } catch {
          // ignore
        }
        await loadMessages();
        messagingService.refreshUnreadCountNow(user.uid);
        return;
      }

      const toMark = conv.messages.filter((m) => !m.isSent && !m.isRead && !m.isDeleted);
      const ids = toMark.map((m) => m.id).filter(Boolean);
      if (ids.length === 0) return;

      const nowIso = new Date().toISOString();
      setMessages((prev) =>
        prev.map((m) =>
          ids.includes(m.id) ? { ...m, isRead: true, readAt: m.readAt ?? nowIso } : m,
        ),
      );
      messagingService.decreaseUnreadCountNow(user.uid, ids.length);

      const results = await Promise.allSettled(ids.map((id) => messagingService.markAsRead(id)));
      const anyRejected = results.some((r) => r.status === 'rejected');
      await loadMessages();
      messagingService.refreshUnreadCountNow(user.uid);
      if (anyRejected) {
        // eslint-disable-next-line no-console
        console.warn('Some messages failed to mark as read; list refreshed from server.');
      }
    },
    [conversations, user?.uid, loadMessages, pendingGroupThread?.threadId],
  );

  useLayoutEffect(() => {
    const justOpened = open && !prevDialogOpenRef.current;
    if (justOpened) {
      suppressAutoSelectLastConvRef.current = false;
    }
    prevDialogOpenRef.current = open;
  }, [open]);

  /** בפתיחת הדיאלוג — לבחור את השיחה העדכנית ביותר (ראשונה ברשימה) לפני ציור, ללא מסך ריק */
  useLayoutEffect(() => {
    if (!open || !user?.uid) return;
    if (messagesListLoading) return;
    if (conversations.length === 0) return;
    if (pendingContactThread || pendingGroupThread || pendingBroadcastContacts) return;
    if (selectedContactId != null) return;
    if (suppressAutoSelectLastConvRef.current) return;
    void handleSelectConv(conversations[0].contactId);
  }, [
    open,
    user?.uid,
    messagesListLoading,
    conversations,
    pendingContactThread,
    pendingGroupThread,
    pendingBroadcastContacts,
    selectedContactId,
    handleSelectConv,
  ]);

  const handlePickContactToMessage = useCallback((contact: MessageContactOption | null) => {
    if (!contact) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setPendingGroupThread(null);
    setPendingBroadcastContacts(null);
    setPendingContactThread(contact);
    setSelectedContactId(contact.id);
    setReplyText('');
    setContactPickerInput('');
    setMultiPickerValue([]);
  }, []);

  const handleStartMultiBroadcast = useCallback(() => {
    if (multiPickerValue.length < 2) return;
    broadcastRecipientsRef.current = multiPickerValue;
    setPendingContactThread(null);
    setPendingGroupThread(null);
    setPendingBroadcastContacts(multiPickerValue);
    setSelectedContactId(BROADCAST_THREAD_ID);
    setReplyText('');
    setMultiPickerValue([]);
    setContactPickerInput('');
    setMultiPickerMode(false);
  }, [multiPickerValue]);

  const handleCreateGroup = useCallback(async () => {
    if (groupMembersInput.length < 1 || !groupTitleInput.trim() || !user?.uid) return;
    const title = groupTitleInput.trim();
    const threadId = `${GROUP_THREAD_PREFIX}title:${title}`;
    let conversationId = '';
    try {
      const created = await messagingService.createGroupConversation({
        fromUserId: user.uid,
        fromUserName: user.displayName || user.email || 'משתמש',
        fromUserRole: String(role || 'viewer'),
        fromUserPhotoURL: user.photoURL || '',
        title,
        members: groupMembersInput.map((m) => ({
          userId: m.id,
          userName: m.fullName || m.email || 'משתמש',
          userRole: m.role || 'viewer',
          userPhotoURL: m.photoURL || '',
        })),
      });
      conversationId = String(created?.id || '').trim();
    } catch (error) {
      console.error('Error creating group conversation:', error);
    }

    const selectedThreadId = conversationId ? `${GROUP_THREAD_PREFIX}${conversationId}` : threadId;
    setPendingGroupThread({
      threadId: selectedThreadId,
      conversationId,
      title,
      members: groupMembersInput,
    });
    groupMembersCacheRef.current.set(selectedThreadId, groupMembersInput);
    if (conversationId) {
      groupMembersCacheRef.current.set(conversationId, groupMembersInput);
      void refreshServerGroupConversations();
    }
    setGroupMembersCacheTick((t) => t + 1);
    setPendingContactThread(null);
    setPendingBroadcastContacts(null);
    setSelectedContactId(selectedThreadId);
    setMultiPickerMode(false);
    setMultiPickerValue([]);
    setContactPickerInput('');
    setGroupDialogOpen(false);
    setGroupTitleInput('');
    setGroupMembersInput([]);
    setReplyText('');
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    focusReplyInput();
  }, [groupMembersInput, groupTitleInput, user?.uid, user?.displayName, user?.email, user?.photoURL, role, focusReplyInput, refreshServerGroupConversations]);

  const handleSend = useCallback(async () => {
    if ((!replyText.trim() && !inlineAttachment) || !selectedConv || !user) return;
    const optimisticId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const nowIso = new Date().toISOString();
    try {
      setSending(true);
      const isFirstInThread = selectedConv.messages.length === 0;
      let subject: string;
      if (isFirstInThread) {
        subject = 'ללא נושא';
      } else {
        const baseSubject = (selectedConv.lastMessage?.subject || '').trim() || 'ללא נושא';
        subject = baseSubject.startsWith('תגובה:') ? baseSubject : `תגובה: ${baseSubject}`;
      }
      const contentText = replyText.trim();
      let targetGroupThreadId: string | null = null;
      const groupMembers = pendingGroupThread?.members || [];
      const isPendingGroup =
        Boolean(selectedConv.isGroup) &&
        !selectedConv.conversationId &&
        groupMembers.length > 0;
      const isGroup = Boolean(
        selectedConv.isGroup && (selectedConv.conversationId || isPendingGroup),
      );
      const broadcastRecipients =
        pendingBroadcastContacts ||
        broadcastRecipientsRef.current ||
        (multiPickerValue.length >= 2 ? multiPickerValue : null);
      const isBroadcast = Boolean(broadcastRecipients && broadcastRecipients.length >= 2);

      /** מזהה ההודעה האמיתי מהשרת — משמש להחליף את ה-id הזמני של ההודעה האופטימית
       * בלי לטעון מחדש את כל ההיסטוריה (ראו הערה ליד setMessages בהמשך). */
      let sentMessageId: string | undefined;

      setUploadingInlineAttachment(Boolean(inlineAttachment));
      if (!isBroadcast) {
        setMessages((prev) => [
          ...prev,
          {
            id: optimisticId,
            fromUserId: user.uid,
            fromUserName: user.displayName || user.email || 'משתמש',
            fromUserPhotoURL: user.photoURL || '',
            fromUserRole: (role as string) ?? 'viewer',
            toUserId: isGroup ? GROUP_MESSAGE_TO_USER_ID : selectedConv.contactId,
            toUserName: selectedConv.contactName,
            subject,
            content: contentText,
            attachmentName: inlineAttachment?.name,
            attachmentType: inlineAttachment?.type,
            attachmentSize: inlineAttachment?.size,
            attachmentIsImage: Boolean(inlineAttachment?.type?.startsWith('image/')),
            isRead: false,
            isDelivered: false,
            createdAt: nowIso,
            replyToMessageId: replyToMessage?.id,
            isSent: true,
            conversationId: selectedConv.conversationId,
            conversationType: isGroup ? 'group' : undefined,
            conversationTitle: isGroup ? selectedConv.contactName : undefined,
          } as Message,
        ]);
      }

      const basePayload = {
        fromUserId: user.uid,
        fromUserName: user.displayName || user.email || 'משתמש',
        fromUserPhotoURL: user.photoURL || '',
        fromUserRole: (role as string) ?? 'viewer',
        subject,
        content: contentText,
        attachmentFile: inlineAttachment,
        replyToMessageId: replyToMessage?.id,
      };

      if (isBroadcast && broadcastRecipients) {
        const recipients = broadcastRecipients.map((c) => ({
          userId: c.id,
          userName: c.fullName || c.email || 'משתמש',
        }));
        await messagingService.sendToRecipients(basePayload as any, recipients);
        setPendingBroadcastContacts(null);
        broadcastRecipientsRef.current = null;
        setMultiPickerValue([]);
      } else if (isGroup) {
        const recipients = groupMembers.map((m) => ({
          userId: m.id,
          userName: m.fullName || m.email || 'משתמש',
        }));
        const memberRoles = Object.fromEntries(groupMembers.map((m) => [m.id, m.role]));
        const memberPhotos = Object.fromEntries(
          groupMembers.map((m) => [m.id, m.photoURL || '']),
        );
        if (selectedConv.conversationId) {
          targetGroupThreadId = `${GROUP_THREAD_PREFIX}${selectedConv.conversationId}`;
          const apiAsOptions: MessageContactOption[] = groupApiMembers.map((m) => ({
            id: m.userId,
            fullName: m.userName,
            email: '',
            role: 'viewer',
          }));
          let cachedMembers =
            groupMembersCacheRef.current.get(targetGroupThreadId) ||
            groupMembersCacheRef.current.get(selectedConv.conversationId) ||
            groupMembers ||
            apiAsOptions;
          if (cachedMembers.length === 0 && user?.uid) {
            const serverConvs = await messagingService.getUserConversations(user.uid);
            const found = serverConvs.find((c) => c.id === selectedConv.conversationId);
            if (found?.members?.length) {
              cachedMembers = found.members.map((m) => ({
                id: m.userId,
                fullName: m.userName || 'משתמש',
                email: '',
                role: m.userRole || 'viewer',
                photoURL: m.userPhotoURL,
              }));
            }
          }
          const memberIds = cachedMembers.map((m) => m.id).filter((id) => id && id !== user.uid);
          sentMessageId = await messagingService.sendMessage({
            ...basePayload,
            conversationId: selectedConv.conversationId,
            toUserId: GROUP_MESSAGE_TO_USER_ID,
            toUserName: selectedConv.contactName,
            toUserIds: memberIds,
            toUserNames: Object.fromEntries(
              cachedMembers.map((m) => [m.id, m.fullName || m.email || 'משתמש']),
            ),
            memberRoles: Object.fromEntries(cachedMembers.map((m) => [m.id, m.role])),
            memberPhotos: Object.fromEntries(cachedMembers.map((m) => [m.id, m.photoURL || ''])),
          } as any);
        } else {
          const created = await messagingService.sendGroupFirstMessage(
            { ...basePayload, memberRoles, memberPhotos } as any,
            recipients,
            selectedConv.contactName,
          );
          const convId = String(created.conversationId || '').trim();
          if (convId) {
            targetGroupThreadId = `${GROUP_THREAD_PREFIX}${convId}`;
            groupMembersCacheRef.current.set(targetGroupThreadId, groupMembers);
            groupMembersCacheRef.current.set(convId, groupMembers);
            setGroupMembersCacheTick((t) => t + 1);
            setSelectedContactId(targetGroupThreadId);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === optimisticId
                  ? {
                      ...m,
                      id: created.id || m.id,
                      conversationId: convId,
                      conversationType: 'group',
                      conversationTitle: selectedConv.contactName,
                      toUserId: GROUP_MESSAGE_TO_USER_ID,
                    }
                  : m,
              ),
            );
          } else {
            targetGroupThreadId = `${GROUP_THREAD_PREFIX}title:${selectedConv.contactName}`;
            setSelectedContactId(targetGroupThreadId);
          }
        }
        setPendingGroupThread(null);
        focusReplyInput();
      } else {
        sentMessageId = await messagingService.sendMessage({
          ...basePayload,
          toUserId: selectedConv.contactId,
          toUserName: selectedConv.contactName,
        } as any);
      }

      setReplyText('');
      setInlineAttachment(null);
      setReplyToMessage(null);
      if (isBroadcast) {
        // אין הודעה אופטימית להשוות אליה בשידור לכמה נמענים בבת אחת — טעינה מחדש
        // היא הדרך היחידה להציג אותן (מקרה נדיר, לא זרימת השליחה הרגילה).
        await loadMessages();
      } else if (sentMessageId) {
        // מחליפים רק את ה-id הזמני של ההודעה האופטימית במזהה האמיתי מהשרת — בלי
        // לטעון מחדש את כל היסטוריית ההודעות. טעינה מחדש בכל שליחה (היה כאן קודם)
        // גרמה לכל השיחה "להבהב"/להיטען מחדש ולגלול מחדש בכל הודעה שנשלחת.
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? { ...m, id: sentMessageId as string } : m)),
        );
      }
      void refreshServerGroupConversations();
    } catch (err: unknown) {
      console.error('Error sending reply:', err);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      const ax = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const status = Number(ax?.response?.status || 0);
      const detail = String(ax?.response?.data?.error || ax?.message || '').trim();
      if (status === 404 && detail.includes('Conversation')) {
        alert(
          'הקבוצה לא נמצאה בשרת. צור קבוצה חדשה, או ודא שה-NAS עודכן (docker compose build api).',
        );
      } else {
        alert(
          detail
            ? `שגיאה בשליחת ההודעה: ${detail}`
            : 'שגיאה בשליחת ההודעה. ודא שה-NAS פעיל (10.0.0.13:3001) ושהשרת נבנה מחדש.',
        );
      }
    } finally {
      setUploadingInlineAttachment(false);
      setSending(false);
    }
  }, [
    replyText,
    inlineAttachment,
    selectedConv,
    user,
    role,
    replyToMessage?.id,
    loadMessages,
    pendingBroadcastContacts,
    pendingGroupThread,
    groupApiMembers,
    refreshServerGroupConversations,
    focusReplyInput,
    multiPickerValue,
  ]);

  const handleDelete = useCallback(async (msgId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) return;
    await messagingService.deleteMessage(msgId).catch(console.error);
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    if (!user?.uid) return;
    const uid = user.uid;
    const nowIso = new Date().toISOString();
    setMessages((prev) =>
      prev.map((m) =>
        m.toUserId === uid && !m.isSent && !m.isRead && !m.isDeleted
          ? { ...m, isRead: true, readAt: m.readAt ?? nowIso }
          : m,
      ),
    );
    const unreadInDialog = messages.filter(
      (m) => m.toUserId === uid && !m.isSent && !m.isRead && !m.isDeleted,
    ).length;
    if (unreadInDialog > 0) {
      messagingService.decreaseUnreadCountNow(uid, unreadInDialog);
    }
    try {
      await messagingService.markAllAsRead(uid);
      await loadMessages();
      messagingService.refreshUnreadCountNow(uid);
    } catch (err) {
      console.error(err);
      await loadMessages();
      messagingService.refreshUnreadCountNow(uid);
    }
  }, [user?.uid, loadMessages, messages]);

  const handleEditMessage = useCallback(async () => {
    if (!editingMessageId || !editDraft.trim()) return;
    try {
      await messagingService.editMessage(editingMessageId, editDraft.trim());
      setEditingMessageId(null);
      setEditDraft('');
      await loadMessages();
    } catch (error) {
      console.error('Error editing message:', error);
      alert('שגיאה בעריכת ההודעה');
    }
  }, [editingMessageId, editDraft, loadMessages]);

  const openMessageActions = useCallback((event: React.MouseEvent<HTMLElement>, message: Message) => {
    setActionsAnchorEl(event.currentTarget);
    setActiveMessageForActions(message);
  }, []);

  const closeMessageActions = useCallback(() => {
    setActionsAnchorEl(null);
    setActiveMessageForActions(null);
  }, []);

  const renderMessageContent = useCallback(
    (text: string) => {
      const source = String(text || '');
      const parts = source.split(/(#\d{6,})/g).filter(Boolean);
      return parts.map((part, index) => {
        const match = part.match(/^#(\d{6,})$/);
        if (!match) {
          return <React.Fragment key={`txt-${index}`}>{part}</React.Fragment>;
        }
        const callNumber = match[1];
        return (
          <Box
            key={`tag-${index}`}
            component="span"
            onClick={() => {
              navigate('/ServiceCallsPage', {
                state: { prefillSearch: callNumber },
              });
              onClose();
            }}
            sx={{
              color: 'warning.light',
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              mx: 0.25,
            }}
          >
            #{callNumber}
          </Box>
        );
      });
    },
    [navigate, onClose],
  );

  const loadRepairOptions = useCallback(async (query: string) => {
    try {
      setRepairLoading(true);
      const response = await fetchRepairsFromHybrid({
        limit: 20,
        offset: 0,
        q: String(query || '').trim() || undefined,
      });
      const mapped: QuickRepairOption[] = (response.data || [])
        .map((r: any) => ({
          id: String(r.id || ''),
          callNumber: String(r.callNumber || '').trim(),
          customerName: String(r.customerName || '').trim(),
          deviceModel: String(r.deviceModel || '').trim(),
          status: String(r.status || '').trim(),
        }))
        .filter((r) => r.callNumber);
      setRepairOptions(mapped);
    } catch (error) {
      console.error('Error loading repairs for picker:', error);
      setRepairOptions([]);
    } finally {
      setRepairLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!repairPickerOpen) return;
    void loadRepairOptions(debouncedRepairSearch);
  }, [repairPickerOpen, debouncedRepairSearch, loadRepairOptions]);

  // ─── Colors ──────────────────────────────────────────────────────────────────

  const sentBg = theme.palette.primary.main;
  const recvBg = isDark ? theme.palette.grey[700] : '#e9f0fe';
  const chatBg = isDark ? '#1a1a2e' : '#e8eaf0';
  const panelBg = isDark ? theme.palette.grey[900] : '#fff';
  const barBg = isDark ? theme.palette.grey[800] : '#f0f2f5';

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            direction: 'rtl',
            height: '88vh',
            maxHeight: '860px',
            borderRadius: { xs: 0, sm: 3 },
            overflow: 'hidden',
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        {/* ── Title ── */}
        <DialogTitle sx={{ p: 0, bgcolor: theme.palette.primary.main, flexShrink: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 1.5 }}
          >
            <Stack direction="row" alignItems="center">
              <ForumIcon sx={{ color: 'white', fontSize: 26, ml: 1.5 }} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    color: 'white',
                    lineHeight: 1.05,
                    fontSize: '1.08rem',
                    letterSpacing: 1.1,
                    fontFamily: '"Trebuchet MS", "Segoe UI", "Arial Black", sans-serif',
                    textShadow: '0 1px 10px rgba(0,0,0,0.28)',
                  }}
                >
                  WHATSTAR
                </Typography>
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), display: 'block', lineHeight: 1.2 }}>
                  מערכת הודעות פנימית
                </Typography>
                {totalUnread > 0 && (
                  <Typography variant="caption" sx={{ color: alpha('#fff', 0.85) }}>
                    {totalUnread} הודעות חדשות
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        {/* ── Body ── */}
        <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: 1, height: 0 }}>
          <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>

              {/* ── Left panel: Conversation list ── */}
              <Box
                sx={{
                  width: { xs: '100%', sm: 'clamp(270px, 30%, 340px)' },
                  display: { xs: selectedContactId ? 'none' : 'flex', sm: 'flex' },
                  flexDirection: 'column',
                  borderLeft: { xs: 0, sm: 1 },
                  borderColor: 'divider',
                  bgcolor: panelBg,
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                {/* Search */}
                <Box sx={{ p: 1.5, bgcolor: barBg, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ mb: 1, gap: 1.25, flexWrap: 'wrap', justifyContent: 'flex-start' }}
                  >
                    <Tooltip title="קבוצה חדשה">
                      <IconButton
                        size="small"
                        onClick={() => setGroupDialogOpen(true)}
                        sx={{
                          bgcolor: isDark ? alpha('#fff', 0.08) : 'white',
                          border: 1,
                          borderColor: 'divider',
                          mr: 0.25,
                        }}
                      >
                        <GroupAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={multiPickerMode ? 'ביטול שליחה למספר נמענים' : 'שליחה למספר נמענים'}>
                      <IconButton
                        size="small"
                        color={multiPickerMode ? 'primary' : 'default'}
                        onClick={() => {
                          setMultiPickerMode((v) => !v);
                          setMultiPickerValue([]);
                          setContactPickerInput('');
                        }}
                        sx={{
                          bgcolor: multiPickerMode
                            ? alpha(theme.palette.primary.main, 0.12)
                            : isDark
                              ? alpha('#fff', 0.08)
                              : 'white',
                          border: 1,
                          borderColor: multiPickerMode ? 'primary.main' : 'divider',
                          ml: 0.25,
                        }}
                      >
                        <PeopleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {multiPickerMode && multiPickerValue.length >= 2 && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleStartMultiBroadcast}
                        sx={{ whiteSpace: 'nowrap', mr: 0.5 }}
                      >
                        שלח ל־{multiPickerValue.length}
                      </Button>
                    )}
                  </Stack>
                  <Autocomplete
                    multiple={multiPickerMode}
                    options={messageContactOptions}
                    inputValue={contactPickerInput}
                    onInputChange={(_e, v) => setContactPickerInput(v)}
                    onChange={(_e, v) => {
                      if (multiPickerMode) {
                        setMultiPickerValue(Array.isArray(v) ? v : []);
                        return;
                      }
                      handlePickContactToMessage(Array.isArray(v) ? v[0] : v);
                    }}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      const name = contactOptionLabel(option);
                      return multiPickerMode ? name : `${name} (${getRoleLabel(option.role)})`;
                    }}
                    isOptionEqualToValue={isSameContactOption}
                    value={multiPickerMode ? multiPickerValue : null}
                    blurOnSelect={!multiPickerMode}
                    disableCloseOnSelect={multiPickerMode}
                    size="small"
                    sx={{ width: '100%' }}
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={contactOptionLabel(option)}
                          size="small"
                        />
                      ))
                    }
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                      <Box component="li" key={key} {...optionProps}>
                        <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
                          <Avatar
                            src={option.photoURL}
                            sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                          >
                            {(option.fullName || option.email).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box flex={1} sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {option.fullName || option.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {getRoleLabel(option.role)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          multiPickerMode
                            ? 'בחר מספר אנשים (2+) ולחץ «שלח ל-N»'
                            : 'שלח הודעה לאיש קשר...'
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end">
                                <PersonSearchIcon sx={{ color: 'text.secondary', fontSize: 18, ml: 0.5 }} />
                              </InputAdornment>
                            </>
                          ),
                          sx: {
                            borderRadius: 5,
                            bgcolor: isDark ? theme.palette.grey[700] : 'white',
                            fontSize: '0.85rem',
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              borderColor: theme.palette.text.disabled,
                            },
                            '&.Mui-focused': {
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                            '& .MuiOutlinedInput-input': {
                              paddingTop: 0.6,
                              paddingBottom: 0.6,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Mark all read */}
                {totalUnread > 0 && (
                  <Box sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Button
                      size="small"
                      startIcon={<DraftsIcon sx={{ ml: 0.5, fontSize: 16 }} />}
                      onClick={handleMarkAllRead}
                      sx={{ fontSize: '0.75rem', py: 0.4 }}
                    >
                      סמן הכל כנקרא ({totalUnread})
                    </Button>
                  </Box>
                )}

                {/* Conversations */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {messagesListLoading && conversations.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 6,
                        minHeight: 140,
                      }}
                    >
                      <CircularProgress size={36} />
                    </Box>
                  ) : conversations.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <ForumIcon sx={{ fontSize: 52, color: 'text.secondary', opacity: 0.3, mb: 1.5 }} />
                      <Typography color="text.secondary" variant="body2">
                        אין שיחות עדיין
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', maxWidth: 260, mx: 'auto' }}>
                        כדי להתחיל — בחר איש קשר בשדה &quot;שלח הודעה לאיש קשר&quot; למעלה
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {conversations.map((conv) => {
                        const lastMsg = conv.lastMessage;
                        const isSelected = conv.contactId === selectedContactId;
                        const hasUnread = conv.unreadCount > 0;
                        return (
                          <ListItem
                            key={conv.contactId}
                            button
                            onClick={() => handleSelectConv(conv.contactId)}
                            sx={{
                              py: 1.3,
                              px: 2,
                              bgcolor: isSelected
                                ? isDark
                                  ? alpha(theme.palette.primary.main, 0.2)
                                  : '#eef2ff'
                                : 'transparent',
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              cursor: 'pointer',
                              transition: 'background-color 0.15s',
                              alignItems: 'center',
                              '&:hover': {
                                bgcolor: isDark
                                  ? alpha(theme.palette.primary.main, 0.12)
                                  : '#f5f7ff',
                              },
                            }}
                          >
                            {/* Avatar בצד ימין (RTL) */}
                            <ListItemAvatar
                              sx={{
                                minWidth: 0,
                                ml: 1.5,
                                alignSelf: 'flex-start',
                              }}
                            >
                              <Badge
                                badgeContent={conv.unreadCount}
                                color="primary"
                                invisible={!hasUnread}
                                overlap="circular"
                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                              >
                                <Avatar
                                  src={conv.contactPhoto}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    bgcolor: getAvatarColor(conv.contactName),
                                    fontWeight: 700,
                                    fontSize: '1.05rem',
                                  }}
                                >
                                  {conv.isGroup ? (
                                    <GroupsIcon sx={{ fontSize: 22 }} />
                                  ) : (
                                    conv.contactName.charAt(0).toUpperCase()
                                  )}
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>

                            {/* טקסט מסודר בצד שמאל */}
                            <Box
                              sx={{
                                flex: 1,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.3,
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ minWidth: 0 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: hasUnread ? 700 : 500,
                                    color: 'text.primary',
                                    fontSize: '0.875rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {conv.contactName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flexShrink: 0,
                                    color: hasUnread ? 'primary.main' : 'text.secondary',
                                    fontWeight: hasUnread ? 600 : 400,
                                    fontSize: '0.7rem',
                                    pl: 1,
                                  }}
                                >
                                  {lastMsg?.createdAt ? formatConvDate(lastMsg.createdAt) : ''}
                                </Typography>
                              </Stack>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ overflow: 'hidden', minWidth: 0 }}
                              >
                                {lastMsg?.isSent && !lastMsg.isRead && (
                                  <DoneIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'text.disabled',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                {lastMsg?.isSent && lastMsg.isRead && (
                                  <DoneAllIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'primary.main',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: hasUnread ? 'text.primary' : 'text.secondary',
                                    fontWeight: hasUnread ? 600 : 400,
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {lastMsg?.content
                                    ? lastMsg.content
                                    : lastMsg?.attachmentName
                                    ? `📎 ${lastMsg.attachmentName}`
                                    : conv.isGroup
                                    ? 'קבוצה חדשה'
                                    : ''}
                                </Typography>
                              </Stack>
                            </Box>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Box>

              {/* ── Right panel: Chat view ── */}
              <Box
                sx={{
                  flex: 1,
                  display: { xs: selectedContactId ? 'flex' : 'none', sm: 'flex' },
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minWidth: 0,
                  bgcolor: chatBg,
                }}
              >
                {!selectedConv ? (
                  /* Empty state */
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      gap: 2,
                      p: 3,
                    }}
                  >
                    <ForumIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.2 }} />
                    {messagesListLoading ? (
                      <CircularProgress size={40} />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.45, textAlign: 'center', maxWidth: 320 }}>
                        בחר שיחה מהרשימה, או חפש איש קשר בשדה למעלה כדי לפתוח שיחה
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <>
                    {/* Chat header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        bgcolor: barBg,
                        borderBottom: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    >
                      {/* Back button (mobile) */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          suppressAutoSelectLastConvRef.current = true;
                          setSelectedContactId(null);
                        }}
                        sx={{ display: { sm: 'none' } }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                      <Avatar
                        src={selectedConv.contactPhoto}
                        sx={{
                          width: 42,
                          height: 42,
                          bgcolor: getAvatarColor(selectedConv.contactName),
                          fontWeight: 700,
                          cursor: 'default',
                        }}
                      >
                        {selectedConv.isGroup ? (
                          <GroupsIcon />
                        ) : (
                          selectedConv.contactName.charAt(0).toUpperCase()
                        )}
                      </Avatar>
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                          {selectedConv.contactName}
                        </Typography>
                        {selectedConv.isGroup || selectedContactId?.startsWith(GROUP_THREAD_PREFIX) ? (
                          selectedGroupMemberLabels.length > 0 ? (
                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedGroupMemberLabels.map((m) => (
                                <Chip
                                  key={m.id}
                                  label={m.name}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem', maxWidth: '100%' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                              קבוצה · טוען משתתפים...
                            </Typography>
                          )
                        ) : selectedConv.contactRole ? (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            {getRoleLabel(selectedConv.contactRole)}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {selectedConv.messages.length} הודעות
                      </Typography>
                    </Box>

                    {/* Messages area */}
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                      }}
                    >
                      {selectedConv.messages.map((msg, idx) => {
                        const isSent = msg.isSent;
                        const msgs = selectedConv.messages;
                        const repliedMessage = msg.replyToMessageId
                          ? msgs.find((m) => m.id === msg.replyToMessageId)
                          : null;

                        const showDate =
                          idx === 0 ||
                          new Date(msg.createdAt as string).toDateString() !==
                            new Date(msgs[idx - 1].createdAt as string).toDateString();

                        const isGroupChat =
                          Boolean(selectedConv.isGroup) ||
                          Boolean(selectedContactId?.startsWith(GROUP_THREAD_PREFIX));

                        const isLastInGroup = isGroupChat
                          ? idx === msgs.length - 1 ||
                            String(msgs[idx + 1]?.fromUserId || '') !== String(msg.fromUserId || '')
                          : idx === msgs.length - 1 || msgs[idx + 1].isSent !== msg.isSent;

                        const showSenderName = isGroupChat;

                        const senderDisplayName =
                          (msg.isSent
                            ? user?.displayName || user?.email
                            : resolveContactById(msg.fromUserId)?.name) ||
                          msg.fromUserName ||
                          'משתמש';

                        return (
                          <React.Fragment key={msg.id}>
                            {/* Date separator */}
                            {showDate && (
                              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
                                <Chip
                                  label={new Date(msg.createdAt as string).toLocaleDateString('he-IL', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                  })}
                                  size="small"
                                  sx={{
                                    bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.07),
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    height: 24,
                                    color: 'text.secondary',
                                  }}
                                />
                              </Box>
                            )}

                            {/* Message row */}
                            <Box
                              sx={{
                                display: 'flex',
                                // RTL: flex-start = right side (my messages), flex-end = left side (their messages)
                                justifyContent: isSent ? 'flex-start' : 'flex-end',
                                mb: isLastInGroup ? 1 : 0.2,
                                '&:hover .msg-del': { opacity: 1 },
                              }}
                            >
                              <Box sx={{ maxWidth: '72%', position: 'relative' }}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    px: 1.75,
                                    py: 0.875,
                                    bgcolor: isSent ? sentBg : recvBg,
                                    color: isSent ? 'white' : 'text.primary',
                                    borderRadius: isSent
                                      ? isLastInGroup
                                        ? '18px 18px 4px 18px'
                                        : '18px'
                                      : isLastInGroup
                                      ? '18px 18px 18px 4px'
                                      : '18px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                                  }}
                                >
                                  {showSenderName && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mb: 0.35,
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        color: isSent ? alpha('#fff', 0.92) : 'primary.main',
                                      }}
                                    >
                                      {senderDisplayName}
                                    </Typography>
                                  )}

                                  {/* Subject (show for first of a topic) */}
                                  {(idx === 0 ||
                                    msgs[idx - 1]?.subject !== msg.subject) &&
                                    msg.subject &&
                                    !msg.subject.startsWith('תגובה:') && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: 'block',
                                          mb: 0.5,
                                          fontWeight: 600,
                                          opacity: 0.75,
                                          fontSize: '0.7rem',
                                          color: isSent ? alpha('#fff', 0.9) : 'text.secondary',
                                        }}
                                      >
                                        📋 {msg.subject}
                                      </Typography>
                                    )}

                                  {repliedMessage && (
                                    <Box
                                      sx={{
                                        mb: 0.75,
                                        px: 1,
                                        py: 0.6,
                                        borderRadius: 1,
                                        borderRight: '3px solid',
                                        borderColor: isSent ? alpha('#fff', 0.65) : 'primary.main',
                                        bgcolor: isSent ? alpha('#000', 0.15) : alpha('#000', 0.04),
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block', fontWeight: 700, mb: 0.2 }}
                                      >
                                        {repliedMessage.fromUserName}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                        {(repliedMessage.content || repliedMessage.attachmentName || '').slice(0, 70)}
                                      </Typography>
                                    </Box>
                                  )}

                                  {msg.content && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.55,
                                        fontSize: '0.875rem',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {msg.isDeleted ? 'הודעה נמחקה' : renderMessageContent(msg.content)}
                                    </Typography>
                                  )}
                                  {msg.isEdited && !msg.isDeleted && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 0.25,
                                        fontSize: '0.65rem',
                                        color: isSent ? alpha('#fff', 0.75) : 'text.secondary',
                                      }}
                                    >
                                      נערכה
                                    </Typography>
                                  )}

                                  {/* Attachment preview */}
                                  {!msg.isDeleted && msg.attachmentUrl && (
                                    <Box sx={{ mt: msg.content ? 1 : 0.5 }}>
                                      {msg.attachmentIsImage ? (
                                        <Box
                                          component="img"
                                          src={msg.attachmentUrl}
                                          alt={msg.attachmentName || 'תמונה מצורפת'}
                                          onClick={() => {
                                            setPreviewImageUrl(resolveAttachmentUrl(msg.attachmentUrl) || null);
                                            setPreviewImageName(msg.attachmentName || 'תמונה מצורפת');
                                          }}
                                          sx={{
                                            width: '100%',
                                            maxWidth: 260,
                                            maxHeight: 280,
                                            borderRadius: 1,
                                            display: 'block',
                                            objectFit: 'cover',
                                            cursor: 'zoom-in',
                                          }}
                                        />
                                      ) : (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          href={resolveAttachmentUrl(msg.attachmentUrl)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ fontSize: '0.75rem', px: 1.5 }}
                                        >
                                          📎 {msg.attachmentName || 'הורדת קובץ'}
                                        </Button>
                                      )}
                                    </Box>
                                  )}

                                  {/* Time + read tick */}
                                  <Stack
                                    direction="row"
                                    spacing={0.4}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    sx={{ mt: 0.3 }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.68rem',
                                        color: isSent ? alpha('#fff', 0.72) : 'text.disabled',
                                        lineHeight: 1,
                                      }}
                                    >
                                      {formatTime(msg.createdAt)}
                                    </Typography>
                                    {isSent && !msg.isRead && (
                                      msg.isDelivered ? (
                                        <DoneAllIcon
                                          sx={{
                                            fontSize: 13,
                                            color: alpha('#fff', 0.72),
                                          }}
                                        />
                                      ) : (
                                        <DoneIcon
                                          sx={{
                                            fontSize: 13,
                                            color: alpha('#fff', 0.7),
                                          }}
                                        />
                                      )
                                    )}
                                    {isSent && msg.isRead && (
                                      <DoneAllIcon
                                        sx={{
                                          fontSize: 13,
                                          color: '#87ceeb',
                                        }}
                                      />
                                    )}
                                  </Stack>
                                </Paper>

                                {!msg.isDeleted && (
                                  <Tooltip title="פעולות הודעה">
                                    <IconButton
                                      className="msg-del"
                                      size="small"
                                      onClick={(event) => openMessageActions(event, msg)}
                                      sx={{
                                        position: 'absolute',
                                        bottom: 6,
                                        [isSent ? 'left' : 'right']: -14,
                                        opacity: 0.95,
                                        transition: 'opacity 0.15s',
                                        color: isSent ? theme.palette.primary.dark : theme.palette.grey[900],
                                        width: 22,
                                        height: 22,
                                        zIndex: 4,
                                        bgcolor: alpha('#fff', 0.96),
                                        border: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                          opacity: 1,
                                          bgcolor: '#fff',
                                        },
                                      }}
                                    >
                                      <MoreVertIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </Box>

                    {/* Input bar */}
                    <Box
                      sx={{
                        p: 1.25,
                        bgcolor: barBg,
                        borderTop: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    >
                      {replyToMessage && (
                        <Box
                          sx={{
                            mb: 1,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderRight: `3px solid ${theme.palette.primary.main}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              תגובה ל: {replyToMessage.fromUserName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {replyToMessage.content || replyToMessage.attachmentName || ''}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setReplyToMessage(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {editingMessageId && (
                        <Box
                          sx={{
                            mb: 1,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.warning.main, 0.12),
                            borderRight: `3px solid ${theme.palette.warning.main}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            עריכת הודעה
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              fullWidth
                              size="small"
                              value={editDraft}
                              onChange={(e) => setEditDraft(e.target.value)}
                              placeholder="ערוך את ההודעה..."
                            />
                            <Button size="small" variant="contained" onClick={() => void handleEditMessage()}>
                              שמור
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                setEditingMessageId(null);
                                setEditDraft('');
                              }}
                            >
                              ביטול
                            </Button>
                          </Stack>
                        </Box>
                      )}
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <Tooltip title="שיוך קריאה להודעה">
                          <IconButton
                            onClick={() => setRepairPickerOpen(true)}
                            sx={{
                              bgcolor: 'transparent',
                              color: 'text.secondary',
                              width: 38,
                              height: 38,
                            }}
                            disabled={sending || uploadingInlineAttachment}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          component="label"
                          sx={{
                            bgcolor: 'transparent',
                            color: 'text.secondary',
                            width: 38,
                            height: 38,
                          }}
                          disabled={sending || uploadingInlineAttachment}
                        >
                          <AttachFileIcon sx={{ transform: 'rotate(-45deg)' }} />
                          <input
                            type="file"
                            hidden
                            accept="image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setInlineAttachment(file);
                            }}
                          />
                        </IconButton>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TextField
                            inputRef={replyInputRef}
                            fullWidth
                            multiline
                            maxRows={4}
                            size="small"
                            placeholder="הקלד הודעה..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void handleSend();
                              }
                            }}
                            disabled={sending}
                            InputProps={{
                              sx: {
                                borderRadius: 5,
                                bgcolor: isDark ? theme.palette.grey[700] : 'white',
                                fontSize: '0.875rem',
                                // מסגרת חיצונית בלבד – בלי outline פנימי
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                  borderColor: theme.palette.text.disabled,
                                },
                                '&.Mui-focused': {
                                  borderColor: theme.palette.primary.main,
                                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                // שהטקסט וה־placeholder יישבו גבוה ולא באמצע הקופסה, בלי פס אפור למעלה
                                '& .MuiInputBase-inputMultiline': {
                                  paddingTop: 0.2,
                                  paddingBottom: 0.4,
                                },
                              },
                            }}
                          />
                          {inlineAttachment && (
                            <Box
                              sx={{
                                mt: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: 'flex-end',
                              }}
                            >
                              {inlineAttachment.type.startsWith('image/') && (
                                <Box
                                  component="img"
                                  src={URL.createObjectURL(inlineAttachment)}
                                  alt={inlineAttachment.name}
                                  sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 1,
                                    objectFit: 'cover',
                                    border: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
                                  }}
                                />
                              )}
                              {!inlineAttachment.type.startsWith('image/') && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  📎 {inlineAttachment.name}
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => setInlineAttachment(null)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          onClick={handleSend}
                          disabled={
                            (!replyText.trim() && !inlineAttachment) ||
                            sending ||
                            uploadingInlineAttachment
                          }
                          sx={{
                            bgcolor:
                              replyText.trim() || inlineAttachment
                                ? theme.palette.primary.main
                                : 'action.disabledBackground',
                            color: replyText.trim() || inlineAttachment ? 'white' : 'action.disabled',
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            flexShrink: 0,
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              bgcolor: replyText.trim()
                                ? theme.palette.primary.dark
                                : 'action.disabledBackground',
                            },
                          }}
                        >
                          {sending || uploadingInlineAttachment ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SendIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />
                          )}
                        </IconButton>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 0.5, display: 'block', fontSize: '0.67rem' }}
                      >
                        Enter לשליחה · Shift+Enter לשורה חדשה
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { direction: 'rtl' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {previewImageName}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewImageUrl(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 0 }}>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt={previewImageName || 'תמונה'}
              sx={{
                width: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: 1,
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={closeMessageActions}
        PaperProps={{ sx: { direction: 'rtl' } }}
      >
        <MenuItem
          onClick={() => {
            if (activeMessageForActions) setReplyToMessage(activeMessageForActions);
            closeMessageActions();
          }}
        >
          <ReplyIcon sx={{ ml: 1, fontSize: 18 }} />
          השב
        </MenuItem>
        {(activeMessageForActions &&
          !activeMessageForActions.isDeleted &&
          String(activeMessageForActions.fromUserId || '') === String(user?.uid || '')) && (
          <MenuItem
            onClick={() => {
              if (activeMessageForActions) {
                setEditingMessageId(activeMessageForActions.id);
                setEditDraft(activeMessageForActions.content || '');
              }
              closeMessageActions();
            }}
          >
            <EditIcon sx={{ ml: 1, fontSize: 18 }} />
            ערוך
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (activeMessageForActions) void handleDelete(activeMessageForActions.id);
            closeMessageActions();
          }}
        >
          <DeleteIcon sx={{ ml: 1, fontSize: 18, color: 'error.main' }} />
          מחק
        </MenuItem>
      </Menu>

      <Dialog
        open={repairPickerOpen}
        onClose={() => setRepairPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { direction: 'rtl' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            שייך קריאה להודעה
          </Typography>
          <IconButton size="small" onClick={() => setRepairPickerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="חפש לפי מספר קריאה / לקוח / טלפון..."
            value={repairSearch}
            onChange={(e) => setRepairSearch(e.target.value)}
            inputProps={{
              dir: 'rtl',
              style: { textAlign: 'right' },
            }}
            InputProps={{
              endAdornment: repairLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
              sx: {
                borderRadius: 5,
                bgcolor: isDark ? theme.palette.grey[700] : 'white',
                fontSize: '0.875rem',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  borderColor: theme.palette.text.disabled,
                },
                '&.Mui-focused': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-input': {
                  paddingTop: 0.65,
                  paddingBottom: 0.65,
                },
              },
            }}
            sx={{ mb: 1.5, direction: 'rtl' }}
          />

          <List sx={{ maxHeight: 380, overflowY: 'auto', p: 0, direction: 'rtl' }}>
            {repairOptions.map((repair) => (
              <ListItem key={repair.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    setReplyText((prev) => `${prev}${prev ? ' ' : ''}#${repair.callNumber}`);
                    setRepairPickerOpen(false);
                  }}
                  sx={{ borderRadius: 1, mb: 0.5, textAlign: 'right' }}
                >
                  <Box sx={{ width: '100%', textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      #{repair.callNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {repair.customerName || 'ללא לקוח'} · {repair.deviceModel || 'ללא דגם'} · {repair.status || ''}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
            {!repairLoading && repairOptions.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">לא נמצאו קריאות</Typography>
              </Box>
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        dir="rtl"
        disableRestoreFocus
      >
        <DialogTitle sx={{ fontWeight: 700 }}>קבוצה חדשה</DialogTitle>
        <DialogContent>
          <TextField
            label="שם הקבוצה"
            value={groupTitleInput}
            onChange={(e) => setGroupTitleInput(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
          <Autocomplete
            multiple
            options={messageContactOptions}
            value={groupMembersInput}
            onChange={(_e, v) => setGroupMembersInput(v)}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : contactOptionLabel(option)
            }
            isOptionEqualToValue={isSameContactOption}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={contactOptionLabel(option)}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="חברי קבוצה" placeholder="בחר לפחות איש קשר אחד" margin="normal" />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGroupDialogOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            disabled={!groupTitleInput.trim() || groupMembersInput.length < 1}
            onClick={handleCreateGroup}
          >
            צור קבוצה
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessagesDialog;
